/**
 * Rock identity (plan R7): every streamed rock gets a key that is stable across sessions and quality presets, so the
 * system delta record (`SystemDelta.rocks`, M2 mining after the bridge) can name it.
 */

const QUANTUM_INV = 10 // positions quantise to 0.1 m
const TWO_32 = 4294967296

let hashHi = 0
let hashLo = 0

/** FNV-1a 64-bit offset basis, as hi/lo 32-bit halves. */
function reset(): void {
  hashHi = 0xcbf29ce4
  hashLo = 0x84222325
}

/** One FNV-1a 64-bit step: h ^= byte; h *= 0x100000001b3 (= 2^40 + 0x1b3), on 32-bit halves. */
function step(byte: number): void {
  const lo = (hashLo ^ byte) >>> 0
  const p = lo * 0x1b3 // < 2^41: exact in a double
  const carry = Math.floor(p / TWO_32)
  hashLo = p >>> 0
  hashHi = (Math.imul(hashHi, 0x1b3) + carry + (lo << 8)) >>> 0
}

function stepString(s: string): void {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    step(c & 0xff)
    step(c >>> 8)
  }
  step(0x1f) // part separator
}

function stepInt(v: number): void {
  const u = v >>> 0
  step(u & 0xff)
  step((u >>> 8) & 0xff)
  step((u >>> 16) & 0xff)
  step(u >>> 24)
}

/**
 * The rock's key: base-36 of a 53-bit FNV-1a (64-bit, folded) over the field id, the part (`c<n>` for a cluster, the
 * cell key for a background cell) and the rock's part-local position quantised to 0.1 m. Per-rock values are identical
 * across presets, so keys are too. 53 bits keep a field's tens of thousands of rocks collision-free in practice.
 */
export function rockKey(fieldId: string, part: string, x: number, y: number, z: number): string {
  reset()
  stepString(fieldId)
  stepString(part)
  stepInt(Math.round(x * QUANTUM_INV))
  stepInt(Math.round(y * QUANTUM_INV))
  stepInt(Math.round(z * QUANTUM_INV))
  const hi = (hashHi ^ (hashHi >>> 21)) & 0x1fffff
  return (hi * TWO_32 + hashLo).toString(36)
}

/**
 * The rock's stable `FieldLayout.id` (M3 R22i, plan §7 item 11): a 32-bit FNV-1a of its key, so a streamed rock has the
 * same id in every session and on every preset. M2 seeds a rock's composition from its id and names removed rocks by
 * id (`sim.removedRocks`, `removeFieldRock`). 32 bits can collide (the chance of any pair among 5,000 live rocks is
 * about 0.3%); a collision makes two rocks share an id, so mining one out removes the other too. Allocation-free.
 */
export function rockIdOf(key: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}
